/**
 * ComponentPreview — the LIVE cell on a bootstrap card.
 *
 * Renders the REAL kit component for the entry, from the shared preview bags
 * in `previewSamples` (the module graph's cards render the same ones). A
 * component with no live sample draws its ports/recipe graph instead.
 *
 * This file exports only the component — that is what keeps Fast Refresh (and
 * the repo's eslint gate) happy.
 */
import { lookupKit } from '../viewir/registry';
import { ViewIRComposition } from '../viewir/interpreter';
import { buildSessionTokenStyle, buildTokenStyle } from '../viewir/tokens';
import { SAMPLES } from './previewSamples';
import RecipeGraph from './RecipeGraph';
import HeaderPreview from './HeaderPreview';

/**
 * The preview cell. A component with no live sample — `render:"spec"`, or a
 * missing sample — draws its ports/recipe mini-graph instead: never a blank
 * box, because a card that explains nothing is worse than no card. The dashed
 * border is the honest cue that this is the contract, not a live render.
 *
 * A `render:"composed"` component has no code to preview, so it previews the
 * way the runtime renders it: the EXPANSION of its `composition`, under the
 * same props scope. There is no other honest picture of a component that is
 * made of other components.
 */
export default function ComponentPreview({ comp }) {
  const kit = lookupKit(comp.id);
  const sample = SAMPLES[comp.id];
  // A SESSION BLOCK is previewed on the surface it ships on: no accent, no
  // display face, `--vi-*` tokens (see `buildSessionTokenStyle`). Drawing it
  // under a widget's signature colour would be a picture of a rule the block
  // is not allowed to follow.
  const style = kit?.session
    ? buildSessionTokenStyle({ appearance: 'light' })
    : buildTokenStyle({ appearance: 'light', accent: '#6E59F2' });

  // Header 是 PageShell 的布局模式，展示真实组合，不注册并不存在的 JSON 类型。
  if (comp.id === 'PageShell') {
    return <div style={{ ...style, borderRadius: 10, padding: 14 }}><HeaderPreview /></div>;
  }

  if (kit?.composition && sample) {
    return (
      <div style={{
        ...style, borderRadius: 10, padding: 14, overflow: 'hidden',
        position: 'relative', minHeight: sample.height || 92,
        display: 'flex', flexDirection: 'column',
      }}>
        <ViewIRComposition
          type={comp.id}
          nodeId={`preview-${comp.id}`}
          def={kit.composed}
          props={sample.props || {}}
          data={sample.data}
          ctx={sample.ctx || { entries: [], settings: {}, pending: { adds: [] } }}
          slots={sample.slots || null}
        />
      </div>
    );
  }

  if (!kit?.component || comp.render === 'spec' || !sample) {
    return (
      <div style={{
        ...style, borderRadius: 10, padding: 12,
        border: '1px dashed var(--line)',
      }}>
        <RecipeGraph comp={comp} />
      </div>
    );
  }

  const Component = kit.component;
  return (
    <div style={{
      ...style, borderRadius: 10, padding: 14, overflow: 'hidden',
      // `position: relative` so a Sheet / picker overlay attaches to THIS cell
      // rather than escaping to the viewport.
      position: 'relative',
      minHeight: sample.height || 92,
      maxHeight: sample.height || undefined,
      display: 'flex', flexDirection: 'column',
    }}>
      <Component
        nodeId={`preview-${comp.id}`}
        variant={sample.variant || comp.variants?.[0]}
        props={sample.props || {}}
        data={sample.data}
        ctx={sample.ctx || { entries: [], settings: {}, pending: { adds: [] } }}
        emit={() => {}}
        slots={sample.slots || {}}
        childItems={[]}
        renderTile={sample.renderTile}
        renderById={() => null}
      >
        {sample.children || null}
      </Component>
    </div>
  );
}
